import { keyBy, noop } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';

import { snowflakeIntegrationDisplayNameVar } from '@/graphql/reactiveVars';
import { useGuardedContext } from '@/hooks/useGuardedContext';
import { Branding_BrandedFragment } from '@/modules/admin/branding/graphql/Branding.generated';
import {
  FeatureEnablementStatus,
  TenantKind,
  TenantOrgMembershipType,
  TenantProductTier,
  TenantUsageBucket,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  AssetClassFragment,
  EffectiveApplicationConfigurationFragment,
  IntegrationConfigurationFragment,
  TenantInformationQuery,
  useTenantInformationQuery,
} from '../graphql/TenantInformation.generated';

export type SubBrandWithSlideConfigs = NonNullable<
  NonNullable<
    NonNullable<TenantInformationQuery['subBrands']['edges']>[number]
  >['node']
>;

export interface TenantDetails {
  id: string;
  luminaryOrganizationID: string | null;
  orgMembershipType: TenantOrgMembershipType | null;
  isTenantInformationLoaded: boolean;
  kind: TenantKind | null;
  usageBucket: TenantUsageBucket;
  tier: TenantProductTier;
  /**
   * assetIntegrationsEnabled is true if the tenant is *allowed* to connect to any integrations. This is false,
   * for example, for go-tier tenants without the integrations add-on. It is always true for pro-tier tenants.
   * NOTE: THIS IS NOT REFLECTIVE OF WHETHER ANY INTEGRATIONS OR ACTUALLY CONNECTED.
   */
  assetIntegrationsEnabled: boolean;
  projectionFunctionalityEnabled: boolean;
  hasPublishedReviews: boolean;
  isSmartReviewEnabled: boolean;
  applicationConfiguration: EffectiveApplicationConfigurationFragment | null;
  integrationConfiguration: IntegrationConfigurationFragment | null;
  tenantSecurityConfiguration: NonNullable<
    TenantInformationQuery['tenantSecurityConfiguration']
  > | null;
  branding: Branding_BrandedFragment | null;
  displayName: string | null;
  subBrandsById: Record<string, SubBrandWithSlideConfigs>;
  activeSubBrandId: string | null;
  setActiveSubBrandId: (id: string | null) => void;
  hasSubBrands: boolean;
  /**
   * Expose asset classes by id so we can show the display names
   * for each asset class throughout the app
   */
  assetClassesById: Record<string, AssetClassFragment>;
  hasCustomOpenAIAPIKey: boolean;
  trialEndDate: Date | null;
  coverSlideConfig: TenantInformationQuery['coverSlideConfig'];
  dividerSlideConfig: TenantInformationQuery['dividerSlideConfig'];
}

const defaultTenantDetails: TenantDetails = {
  id: '',
  luminaryOrganizationID: null,
  orgMembershipType: null,
  isTenantInformationLoaded: false,
  usageBucket: TenantUsageBucket.Internal,
  tier: TenantProductTier.Pro,
  assetIntegrationsEnabled: true,
  projectionFunctionalityEnabled: true,
  hasPublishedReviews: false,
  isSmartReviewEnabled: true,
  applicationConfiguration: null,
  integrationConfiguration: null,
  tenantSecurityConfiguration: null,
  kind: null,
  branding: null,
  displayName: null,
  subBrandsById: {},
  activeSubBrandId: null,
  setActiveSubBrandId: noop,
  hasSubBrands: false,
  assetClassesById: {},
  hasCustomOpenAIAPIKey: false,
  trialEndDate: null,
  coverSlideConfig: null,
  dividerSlideConfig: null,
};

export const TenantDetailsContext =
  React.createContext<TenantDetails>(defaultTenantDetails);

export function TenantDetailsProvider({ children }: React.PropsWithChildren) {
  const [activeSubBrandId, setActiveSubBrandId] = useState<string | null>(null);
  const [isProviderNameInitialized, setIsProviderNameInitialized] =
    useState(false);

  const { data, loading } = useTenantInformationQuery({
    // this shouldn't need to go to the network, but in a scenario when the cache is fully cleared
    // for some reason, we don't want to prevent it from doing so
    fetchPolicy: 'cache-first',
  });

  const snowflakeDisplayName =
    data?.integrationConfiguration?.snowflakeIntegrationDisplayName ?? null;
  useEffect(() => {
    if (loading) return;

    snowflakeIntegrationDisplayNameVar(snowflakeDisplayName);
    setIsProviderNameInitialized(true);
    return () => {
      snowflakeIntegrationDisplayNameVar(null);
    };
  }, [loading, snowflakeDisplayName]);

  const subBrands = getNodes(data?.subBrands);
  const subBrandsById = useMemo(() => {
    return keyBy(subBrands, 'id');
  }, [subBrands]);

  const handleSetActiveSubBrandId = useCallback(
    (id: string | null) => {
      if (id === activeSubBrandId) return;
      setActiveSubBrandId(id);
    },
    [activeSubBrandId]
  );

  const finalTenantDetails: TenantDetails = useMemo(() => {
    if (!data?.tenantInformation) {
      return defaultTenantDetails;
    }

    const tenantDetails = data.tenantInformation;
    const assetClasses = getNodes(data?.assetClasses);
    const assetClassesById = keyBy(assetClasses, 'id');

    return {
      ...(tenantDetails ?? {}),
      luminaryOrganizationID: tenantDetails.luminaryOrganizationID ?? null,
      orgMembershipType: tenantDetails.orgMembershipType ?? null,
      isTenantInformationLoaded: true,
      isSmartReviewEnabled:
        data.effectiveApplicationConfiguration.smartReviewEnabled,
      applicationConfiguration: data?.effectiveApplicationConfiguration ?? null,
      integrationConfiguration: data?.integrationConfiguration ?? null,
      tenantSecurityConfiguration: data?.tenantSecurityConfiguration ?? null,
      displayName: tenantDetails?.branding.displayName ?? null,
      assetIntegrationsEnabled:
        tenantDetails.assetIntegrationsEnabled ===
        FeatureEnablementStatus.Enabled,
      projectionFunctionalityEnabled:
        tenantDetails.projectionFunctionalityEnabled ===
        FeatureEnablementStatus.Enabled,
      tier: tenantDetails.tier,
      subBrandsById,
      activeSubBrandId,
      setActiveSubBrandId: handleSetActiveSubBrandId,
      hasSubBrands: subBrands.length > 0,
      assetClassesById,
      hasCustomOpenAIAPIKey:
        (data?.openaiSdkConfigsForOpenAI?.edges?.length ?? 0) > 0,
      trialEndDate: tenantDetails.trialEndDate ?? null,
      coverSlideConfig: data?.coverSlideConfig ?? null,
      dividerSlideConfig: data?.dividerSlideConfig ?? null,
    };
  }, [
    data,
    subBrandsById,
    activeSubBrandId,
    handleSetActiveSubBrandId,
    subBrands.length,
  ]);

  // Initialize before children mount: some consumers memoize labels once.
  // The effect also keeps tenant-specific state out of the shared SSR process.
  if (!isProviderNameInitialized) return null;

  return (
    <TenantDetailsContext.Provider value={finalTenantDetails}>
      {children}
    </TenantDetailsContext.Provider>
  );
}

export const useTenantDetailsContext = () => {
  return useGuardedContext(TenantDetailsContext, 'TenantDetailsProvider');
};
